import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useTheme } from "@hooks/useTheme";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import TableWithPagination from "@components/Tables/TableWithPagination";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { formatDateTime, formatDate, formatDateForRequest } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

const SbomMergeList = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { hasDeletePermission } = useRoutePermissions();
  const [sbomMergedList, setSbomMergedList] = useState([]);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.SBOM_MERGE_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [searchFilters, setSearchFilters] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModalDelete, setShowModalDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSbomMergedListLoading, setIsSbomMergedListLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const navigate = useNavigate();
  const isFirstRender = useRef(true);

  const {
    creatorList,
    fetchSearchInfo,
  } = useSearchInfo();

  const searchFields = [
    {
      name: "createrId",
      label: t("project.creator"),
      type: "select",
      placeholder: t("project.creator"),
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      label: t("common.create-at-from"),
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("common.create-at-to"),
      placeholder: t("common.create-at-to"),
      type: "date",
    },
    {
      name: "copyright",
      label: t("common.copyright-info"),
      placeholder: t("common.copyright-info"),
    }
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);


  const columns = [
    "mergedName",
    "createrName",
    "createdAt",
  ];

  const columnConfig = {
    mergedName: {
      headerRender: () => t("project.sbom-merged.merge-sbom-name"),
    },
    createrName: {
      headerRender: () => t("project.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      headerRender: () => t("project.creation-date"),
      customRender: (value) => formatDateTime(value),
    },
  };

  // Controls
  const handleDelete = () => setShowModalDelete(true);
  const handleAdd = () => {
    navigate("/sbom-mergeds/new");
  };
  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };
  const handleCheckboxChange = (newSelectedRows) => setSelectedRows([...newSelectedRows]);
  const onRowClick = (rowData) => navigate(`./${rowData.mergedSbomSeq}`);

  // Search handler
  const searchHandler = useCallback((inputData) => {
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleSort = (columnKey, direction) => {
    setSortColumn(columnKey);
    setSortDirection(direction);
  };

  const getSbomMergedList = async (bodyRequest = {}) => {
    setIsSbomMergedListLoading(true);
    try {
      const {
        pageSize = itemsPerPage,
        startRow = 0,
        sortKey = sortColumn || "createdAt",
        sortOrder = sortDirection || "desc",
        ...filters
      } = bodyRequest;
      const response = await getIndexDBData(STORES.MERGED_SBOMS, {
        pageSize,
        startRow,
        filters,
        sortKey,
        sortOrder,
      });

      if (response && typeof response !== "string") {
        setSbomMergedList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching SBOM merges from IndexedDB:", error);
    } finally {
      setIsSbomMergedListLoading(false);
    }
  };

  const deleteSbomMerged = useMutationData(
    queryKeys.deleteSbomMergeds,
    { method: "DELETE" },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.MERGED_SBOMS, selectedRows);
        useSuccessToast(TOAST.success, selectedRows.length + t("project.sbom-merged.deleted-successfully"));
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        setSelectedRows([]);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        toast.error(t("project.sbom-merged.deleted-failed"), { autoClose: 1000 });
      },
    },
  );

  // Fetch logic
  useEffect(() => {
    fetchSearchInfo && fetchSearchInfo();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [searchFilters, itemsPerPage, sortColumn, sortDirection]);

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined) {
        acc[key] = formatDateForRequest(value);
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    bodyRequest["sortKey"] = sortColumn || "createdAt";
    bodyRequest["sortOrder"] = sortDirection || "desc";
    getSbomMergedList(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteSbomMerged.mutateAsync(selectedRows);
      setSelectedRows([]);
      setShowModalDelete(false);
    } catch (error) {
      console.error("Error deleting merged SBOMs:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="sbom-merge-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="sbom-merge-list-header">
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary} uppercase`} data-testid="sbom-merge-list-title">{t("project.sbom-merged.merged-sbom-list")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="sbom-merge-list-controls">
        <TableHeaderControls
          controlsTestId="sbom-merge-list-table-header-controls"
          searchInputTestId="sbom-merge-list-search-input"
          addButtonTestId="sbom-merge-list-add-button"
          deleteButtonTestId="sbom-merge-list-delete-button"
          advancedSearchButtonTestId="sbom-merge-list-advanced-search-button"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onDeleteProject={handleDelete}
          onAdd={handleAdd}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("project.sbom-merged.create-merged-sbom")}
          titleDeleteButton={t("project.sbom-merged.delete-merged-sbom")}
          isDisplayDeleteButton={hasDeletePermission}
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="sbom-merge-list-advanced-search">
          <AdvancedSearch
            data-testid="sbom-merge-list-advanced-search-panel"
            title={t("project.advancedSearch")}
            searchFields={searchFields}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Table */}
      <div className="flex-grow min-h-0" data-testid="sbom-merge-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={sbomMergedList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isSbomMergedListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="mergedSbomSeq"
          shouldShowGoToPage={true}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          showItemsPerPageSelector={true}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortable={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="sbom-merge-list-delete-modal"
        isOpen={showModalDelete}
        onClose={() => setShowModalDelete(false)}
        onConfirm={handleConfirmDelete}
        title={t("project.sbom-merged.confirm-delete-merged-sbom-title")}
        description={t("project.sbom-merged.confirm-delete-merged-sbom-description", { count: selectedRows.length })}
        cancelText={t("common.cancel-button")}
        confirmText={t("common.delete-button")}
        loading={deleteLoading}
      />
    </div>
  );
};

export default SbomMergeList;
